import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MousePointerClick, ArrowRight, X } from "lucide-react";
import { SIZES } from "@/config/site";
import plano from "@/config/plano.json";
import { Reveal, ChapterTag } from "./Reveal";
import { scrollToFormField } from "@/lib/scroll";

const CAT_COLORS = { S: "#FFF6BF", M: "#FFE600", L: "#F0CB00", XL: "#D9A400", XXL: "#121316" };
const CAT_SIZE_ID = { S: "s", M: "m", L: "l", XL: "xl", XXL: "xxl" };
const IDEAL = {
  S: "Cajas, maletas, ropa y material deportivo.",
  M: "Bicis, equipaje de temporada y cajas.",
  L: "Muebles, mudanzas y almacenamiento familiar.",
  XL: "Mudanzas completas o material de autónomos.",
  XXL: "Empresas, stock o grandes necesidades de almacenamiento.",
};

export const PlanSection = () => {
  const [sel, setSel] = useState(null);
  const units = plano.units;
  const bb = plano.bbox;

  const reserve = (u) => {
    const size = SIZES.find((s) => s.id === CAT_SIZE_ID[u.cat]);
    window.dispatchEvent(
      new CustomEvent("prefill-size", { detail: { size: size ? size.label : u.cat, unit: u.id } })
    );
    scrollToFormField();
  };

  return (
    <section id="plano" data-testid="plan-section" className="py-24 sm:py-32 bg-brand-smoke border-y border-brand-ink/8">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <ChapterTag num="03" label="El centro" />
          <h2 className="font-display font-extrabold tracking-tight text-brand-ink text-3xl sm:text-4xl lg:text-5xl mt-5">
            Elige tu trastero en el plano.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-brand-ink/60 max-w-2xl leading-relaxed">
            Este es el plano definitivo de Calle Perú 2. Toca cualquier trastero para ver
            sus características y reservar prioridad sobre él.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10">
          <div className="bg-white rounded-3xl border border-brand-ink/8 shadow-[0_12px_40px_rgba(18,19,22,0.07)] p-4 sm:p-8">
            <p className="flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.2em] uppercase text-brand-ink/45 mb-5 px-1">
              <MousePointerClick size={15} className="text-brand-ink" />
              Toca un trastero para verlo y reservarlo
            </p>
            <div className="relative" data-testid="plan-map-wrapper">
              <div className="overflow-x-auto [scrollbar-width:thin]">
                <svg
                  viewBox={`${bb.x} ${bb.y} ${bb.w} ${bb.h}`}
                  className="w-full min-w-[760px] h-auto select-none"
                  onClick={() => setSel(null)}
                  role="list"
                  aria-label="Plano interactivo de trasteros"
                >
                <rect x={bb.x} y={bb.y} width={bb.w} height={bb.h} fill="#ffffff" />
                {units.map((u) => {
                  const active = sel?.id === u.id;
                  const fs = 12;
                  return (
                    <g
                      key={u.id}
                      data-testid={`plan-unit-${u.id}`}
                      role="listitem"
                      aria-label={`Trastero ${u.id}, tamaño ${u.cat}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSel(u);
                      }}
                      className="cursor-pointer"
                    >
                      <rect
                        x={u.x + 1}
                        y={u.y + 1}
                        width={u.w - 2}
                        height={u.h - 2}
                        rx={3}
                        fill={CAT_COLORS[u.cat]}
                        stroke={active ? "#121316" : "rgba(18,19,22,0.3)"}
                        strokeWidth={active ? 3.5 : 1}
                      />
                      <text
                        x={u.x + u.w / 2}
                        y={u.y + u.h / 2}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={fs}
                        fontWeight={800}
                        fill={u.cat === "XXL" ? "#FFE600" : "#121316"}
                        style={{ pointerEvents: "none", fontFamily: "Outfit, sans-serif" }}
                      >
                        {u.cat}
                      </text>
                    </g>
                  );
                })}
                </svg>
              </div>
              <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-14 bg-gradient-to-l from-white to-transparent sm:hidden" />
              <p className="sm:hidden mt-3 text-center font-mono text-[10px] font-bold tracking-[0.22em] uppercase text-brand-ink/40">
                Desliza el plano para verlo entero →
              </p>
            </div>
          </div>
        </Reveal>

        <AnimatePresence>
          {sel && (
            <motion.div
              data-testid="plan-unit-panel"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 18 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 bg-brand-ink text-white rounded-3xl p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between shadow-[0_20px_60px_rgba(18,19,22,0.25)]"
            >
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-[11px] font-bold tracking-[0.25em] uppercase text-brand-ink bg-brand-yellow px-3 py-1.5 rounded-full">
                    {sel.id}
                  </span>
                  <span className="font-mono text-[11px] font-bold tracking-[0.25em] uppercase text-white/60 border border-white/20 px-3 py-1.5 rounded-full">
                    Disponible para prereserva
                  </span>
                </div>
                <h3 className="font-display font-black text-2xl sm:text-3xl mt-4 tracking-tight">
                  Trastero {sel.cat} · {sel.sqm.toLocaleString("es-ES")} m²
                </h3>
                <p className="mt-2 text-white/65 text-[15px]">{IDEAL[sel.cat]}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <motion.button
                  data-testid="plan-reserve-button"
                  onClick={() => reserve(sel)}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center gap-2 bg-brand-yellow text-brand-ink font-display font-extrabold tracking-wide px-7 py-4 rounded-full hover:bg-brand-yellowhover transition-colors duration-200"
                >
                  RESERVAR ESTE TRASTERO
                  <ArrowRight size={17} strokeWidth={2.6} />
                </motion.button>
                <button
                  data-testid="plan-panel-close"
                  onClick={() => setSel(null)}
                  aria-label="Cerrar"
                  className="p-3 rounded-full border border-white/20 text-white/70 hover:text-brand-yellow hover:border-brand-yellow transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
